import React, { useState, useCallback, useEffect } from 'react';
import { 
  ChevronLeft, Upload, AlertCircle, FileText, 
  Settings2, Wrench, Cpu, Copy, Download, Loader2,
  CheckCircle, AlertTriangle, ChevronDown, Plus, Trash2,
  Ruler, Target, CircleDot, FileCode, Cog, Box, Save, Shield, RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { controllerConfig } from '@/utils/gcode';
import { materialLibrary } from '@/utils/materials';
import ToolpathViewer3D from '@/components/ToolpathViewer3DWrapper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// STL Materials
const STL_MATERIALS = [
  { key: 'stainless', name: 'Stainless Steel 304/316' },
  { key: 'aluminum', name: 'Aluminum 6061-T6' },
  { key: 'carbon_steel', name: 'Carbon Steel 1045' },
  { key: 'titanium', name: 'Titanium 6Al-4V' },
  { key: 'brass', name: 'Brass C360' },
];

// Workholding options by machine type
const workholdingOptions = {
  lathe: [
    { value: '3-jaw-od', label: '3-Jaw Chuck (OD Grip)' },
    { value: '3-jaw-id', label: '3-Jaw Chuck (ID Grip)' },
    { value: '6-jaw', label: '6-Jaw Chuck' },
    { value: 'collet', label: 'Collet Chuck' },
    { value: 'soft-jaws', label: 'Soft Jaws (Custom)' },
    { value: 'faceplate', label: 'Faceplate' },
  ],
  mill: [
    { value: 'vise', label: 'Machine Vise' },
    { value: 'vise-soft-jaws', label: 'Vise w/ Soft Jaws' },
    { value: 'v-block', label: 'V-Block' },
    { value: 'step-clamps', label: 'Step Clamps' },
    { value: 't-slot', label: 'T-Slot Fixture' },
    { value: 'vacuum', label: 'Vacuum Table' },
  ],
  'lathe-turnmill': [
    { value: '3-jaw-od', label: '3-Jaw Chuck (OD Grip)' },
    { value: '3-jaw-id', label: '3-Jaw Chuck (ID Grip)' },
    { value: '6-jaw', label: '6-Jaw Chuck' },
    { value: 'collet', label: 'Collet Chuck' },
    { value: 'soft-jaws', label: 'Soft Jaws (Custom)' },
  ]
};

// Stock shape options
const stockShapeOptions = {
  lathe: [
    { value: 'round-bar', label: 'Round Bar' },
    { value: 'hex-bar', label: 'Hex Bar' },
    { value: 'tube', label: 'Tube/Pipe' },
    { value: 'casting', label: 'Casting/Forging' },
  ],
  mill: [
    { value: 'plate', label: 'Flat Plate' },
    { value: 'block', label: 'Block' },
    { value: 'round-bar', label: 'Round Bar' },
    { value: 'hex-bar', label: 'Hex Bar' },
    { value: 'flat-steel', label: 'Flat Steel Bar' },
    { value: 'casting', label: 'Casting' },
  ],
  'lathe-turnmill': [
    { value: 'round-bar', label: 'Round Bar' },
    { value: 'hex-bar', label: 'Hex Bar' },
    { value: 'tube', label: 'Tube/Pipe' },
    { value: 'casting', label: 'Casting/Forging' },
  ]
};

// Tool types by machine
const toolTypeOptions = {
  lathe: [
    { value: 'od-turning', label: 'OD Turning (Roughing)', inserts: ['CNMG', 'WNMG', 'DNMG', 'VNMG'] },
    { value: 'od-finishing', label: 'OD Finishing', inserts: ['VNMG', 'DNMG', 'CCMT', 'DCMT'] },
    { value: 'id-boring', label: 'ID Boring', inserts: ['CCMT', 'TCMT', 'DCMT', 'VCMT'] },
    { value: 'threading', label: 'Threading', inserts: ['TPI-60', 'TPI-55', 'METRIC', 'ACME'] },
    { value: 'grooving', label: 'Grooving/Cutoff', inserts: ['GW', 'GP', 'GT', 'DGN'] },
    { value: 'parting', label: 'Parting', inserts: ['GW', 'GP', 'N123'] },
    { value: 'drilling', label: 'Drilling', inserts: ['HSS', 'CARBIDE', 'INSERT'] },
    { value: 'center-drill', label: 'Center Drill', inserts: ['#1', '#2', '#3', '#4'] },
    { value: 'face', label: 'Facing', inserts: ['CNMG', 'SNMG', 'WNMG'] },
  ],
  mill: [
    { value: 'face-mill', label: 'Face Mill', inserts: ['APKT', 'APMT', 'SEKT', 'SNMT'] },
    { value: 'endmill', label: 'End Mill', inserts: ['2FL', '3FL', '4FL', '6FL'] },
    { value: 'roughing-endmill', label: 'Roughing End Mill', inserts: ['4FL', '5FL', '6FL'] },
    { value: 'ball-endmill', label: 'Ball End Mill', inserts: ['2FL', '4FL'] },
    { value: 'drill', label: 'Drill', inserts: ['HSS', 'CARBIDE', 'INSERT'] },
    { value: 'tap', label: 'Tap', inserts: ['SPIRAL-PT', 'SPIRAL-FL', 'FORM'] },
    { value: 'chamfer', label: 'Chamfer Mill', inserts: ['45DEG', '60DEG', '90DEG'] },
    { value: 'thread-mill', label: 'Thread Mill', inserts: ['SINGLE-PT', 'MULTI-FL'] },
  ],
  'lathe-turnmill': [
    { value: 'od-turning', label: 'OD Turning', inserts: ['CNMG', 'WNMG', 'DNMG'] },
    { value: 'id-boring', label: 'ID Boring', inserts: ['CCMT', 'TCMT', 'DCMT'] },
    { value: 'threading', label: 'Threading', inserts: ['TPI-60', 'METRIC'] },
    { value: 'grooving', label: 'Grooving', inserts: ['GW', 'GP'] },
    { value: 'drilling', label: 'Drilling (Main)', inserts: ['HSS', 'CARBIDE'] },
    { value: 'live-drill', label: 'Live Drill (Radial)', inserts: ['HSS', 'CARBIDE'] },
    { value: 'live-endmill', label: 'Live End Mill', inserts: ['2FL', '4FL'] },
    { value: 'live-tap', label: 'Live Tap', inserts: ['SPIRAL-PT', 'FORM'] },
  ]
};

// Feature types for manual entry
const featureTypes = [
  { value: 'od-diameter', label: 'OD Diameter', unit: 'in' },
  { value: 'id-diameter', label: 'ID/Bore Diameter', unit: 'in' },
  { value: 'length', label: 'Length/Depth', unit: 'in' },
  { value: 'face', label: 'Face (Z position)', unit: 'in' },
  { value: 'thread', label: 'Thread', unit: 'TPI or pitch' },
  { value: 'groove', label: 'Groove', unit: 'in' },
  { value: 'chamfer', label: 'Chamfer', unit: 'in x deg' },
  { value: 'radius', label: 'Radius/Fillet', unit: 'in' },
  { value: 'taper', label: 'Taper', unit: 'deg or in/ft' },
  { value: 'hole', label: 'Hole/Drill', unit: 'in dia' },
  { value: 'slot', label: 'Slot/Pocket', unit: 'in' },
  { value: 'profile', label: 'Profile/Contour', unit: 'in' },
];

const CADImportPage = () => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingFeatureIdx, setEditingFeatureIdx] = useState(null);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // Section visibility
  const [showSetup, setShowSetup] = useState(true);
  const [showToolLibrary, setShowToolLibrary] = useState(false);
  const [showFeatures, setShowFeatures] = useState(true);
  
  // Machine setup state
  const [machineSetup, setMachineSetup] = useState({
    machine_type: 'lathe',
    controller: 'fanuc',
    workholding: '3-jaw-od',
    stock_material: 'alu-6061-t6',
    stock_shape: 'round-bar',
    stock_diameter: 2.0,
    stock_length: 4.0,
    stock_width: null,
    stock_height: null,
    bar_pull_length: null,
    part_quantity: 1
  });
  
  // Tool library state
  const [tools, setTools] = useState([]);
  
  // AI generation mode
  const [generationMode, setGenerationMode] = useState('complete'); // 'complete' or 'single_operation'
  const [singleOperation, setSingleOperation] = useState('');
  
  const singleOperationOptions = [
    { value: 'facing', label: 'Facing' },
    { value: 'od_roughing', label: 'OD Roughing (G71/G70)' },
    { value: 'od_finishing', label: 'OD Finishing Only' },
    { value: 'threading', label: 'Threading (G32 Long-Hand)' },
    { value: 'grooving', label: 'Grooving' },
    { value: 'drilling', label: 'Drilling' },
    { value: 'boring', label: 'ID Boring' },
    { value: 'parting', label: 'Parting / Cutoff' },
    { value: 'tapping', label: 'Tapping' },
  ];
  
  const [newTool, setNewTool] = useState({
    tool_number: 1,
    tool_type: 'od-turning',
    description: '',
    insert_type: 'CNMG',
    nose_radius: 0.031,
    insert_width: null,
    tool_diameter: null,
    max_doc: 0.1,
    notes: ''
  });
  
  // Manual feature entry state (dimensions, tolerances)
  const [features, setFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState({
    feature_type: 'od-diameter',
    dimension: '',
    tolerance_plus: 0.005,
    tolerance_minus: 0.005,
    location: '',
    surface_finish: '',
    notes: ''
  });
  
  // Additional notes
  const [additionalNotes, setAdditionalNotes] = useState('');
  
  // Input mode: 'file', 'manual', or 'dxf'
  const [inputMode, setInputMode] = useState('manual');
  
  // AI Model selection
  const [aiModel, setAiModel] = useState('claude-sonnet-4-5');
  
  // DXF tool parameters
  const [dxfTool, setDxfTool] = useState({
    diameter: 0.25,
    hfeed: 20.0,
    vfeed: 5.0,
    maxdoc: 0.05,
    depth: 0.25
  });
  
  // DerpCAM availability
  const [derpcamAvailable, setDerpcamAvailable] = useState(false);
  
  // STL processing settings
  const [stlSettings, setStlSettings] = useState({
    material: 'stainless',
    units: 'mm'
  });
  
  // Check DerpCAM status on mount
  useEffect(() => {
    fetch(`${API_URL}/api/derpcam/status`)
      .then(res => res.json())
      .then(data => setDerpcamAvailable(data.available))
      .catch(() => setDerpcamAvailable(false));
    
    // Load recipe template from SavedPrograms if available
    const template = sessionStorage.getItem('recipe_template');
    if (template) {
      try {
        const recipe = JSON.parse(template);
        if (recipe.machine_setup) {
          setMachineSetup(prev => ({
            ...prev,
            ...recipe.machine_setup
          }));
        }
        if (recipe.tools_used?.length > 0) {
          setTools(recipe.tools_used.map((t, i) => ({
            ...t,
            id: `template-${i}-${Date.now()}`
          })));
          setShowToolLibrary(true);
        }
        if (recipe.source) {
          const modeMap = { 'ai-blueprint': 'file', 'manual': 'manual', 'dxf': 'dxf', 'stl': 'stl', 'step': 'step' };
          setInputMode(modeMap[recipe.source] || 'manual');
        }
      } catch (e) {
        console.error('Failed to load recipe template:', e);
      }
      sessionStorage.removeItem('recipe_template');
    }

    // Load converted file from File Converter if available
    const convertedFile = sessionStorage.getItem('converted_file');
    if (convertedFile) {
      const loadConverted = async () => {
        try {
          const { filename, data, format } = JSON.parse(convertedFile);
          const response = await fetch(data);
          const blob = await response.blob();
          const convertedFileObj = new File([blob], filename, { type: blob.type });
          
          const modeMap = { 'png': 'file', 'jpg': 'file', 'stl': 'stl', 'step': 'step', 'dxf': 'dxf' };
          const mode = modeMap[format] || 'file';
          setInputMode(mode);
          setFile(convertedFileObj);
          
          if (['png', 'jpg'].includes(format)) {
            setFilePreview(data);
          }
        } catch (e) {
          console.error('Failed to load converted file:', e);
        }
      };
      loadConverted();
      sessionStorage.removeItem('converted_file');
    }
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (f) => {
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.dxf', '.dwg', '.stl', '.step', '.stp'];
    const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(ext)) {
      setError(`Unsupported file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    setFile(f);
    setError(null);
    setResult(null);
    
    // Set mode based on file type
    if (ext === '.dxf') {
      setInputMode('dxf');
    } else if (ext === '.stl') {
      setInputMode('stl');
    } else if (ext === '.step' || ext === '.stp') {
      setInputMode('step');
    } else {
      setInputMode('file');
    }
    
    // Create preview for images
    if (['.jpg', '.jpeg', '.png'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setFilePreview(null);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  
  const updateMachineSetup = (key, value) => {
    setMachineSetup(prev => {
      const updated = { ...prev, [key]: value };
      
      // Reset workholding and stock shape when machine type changes
      if (key === 'machine_type') {
        updated.workholding = workholdingOptions[value][0].value;
        updated.stock_shape = stockShapeOptions[value][0].value;
      }
      
      return updated;
    });
  };
  
  // Tool functions
  const addTool = () => {
    if (!newTool.description) {
      alert('Please enter a tool description');
      return;
    }
    
    setTools([...tools, { ...newTool, id: Date.now() }]);
    setNewTool({
      tool_number: tools.length + 2,
      tool_type: toolTypeOptions[machineSetup.machine_type][0].value,
      description: '',
      insert_type: toolTypeOptions[machineSetup.machine_type][0].inserts[0],
      nose_radius: 0.031,
      insert_width: null,
      tool_diameter: null,
      max_doc: 0.1,
      notes: ''
    });
  };

  // Add preset tool from quick-select buttons
  const addPresetTool = (toolType, toolNumStr, description, insertType, noseRadius, diameter = null) => {
    const toolNum = parseInt(toolNumStr.replace('T', ''));
    // Check if tool number already exists
    if (tools.some(t => t.tool_number === toolNum)) {
      return; // Don't add duplicate
    }
    setTools([...tools, {
      id: Date.now(),
      tool_number: toolNum,
      tool_type: toolType,
      description: description,
      insert_type: insertType,
      nose_radius: noseRadius,
      tool_diameter: diameter,
      max_doc: 0.1,
      notes: ''
    }]);
  };
  
  const removeTool = (id) => {
    setTools(tools.filter(t => t.id !== id));
  };
  
  // Feature functions
  const addFeature = () => {
    if (!newFeature.dimension) {
      alert('Please enter a dimension value');
      return;
    }
    
    setFeatures([...features, { ...newFeature, id: Date.now() }]);
    setNewFeature({
      feature_type: 'od-diameter',
      dimension: '',
      tolerance_plus: 0.005,
      tolerance_minus: 0.005,
      location: '',
      surface_finish: '',
      notes: ''
    });
  };
  
  const removeFeature = (id) => {
    setFeatures(features.filter(f => f.id !== id));
  };
  
  // Process DXF file using DerpCAM
  const processDxf = async () => {
    if (!file || inputMode !== 'dxf') {
      setError('Please upload a DXF file');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tool_diameter', dxfTool.diameter);
      formData.append('tool_hfeed', dxfTool.hfeed);
      formData.append('tool_vfeed', dxfTool.vfeed);
      formData.append('tool_maxdoc', dxfTool.maxdoc);
      formData.append('material_depth', dxfTool.depth);
      
      const response = await fetch(`${API_URL}/api/derpcam/process-dxf`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'DXF processing failed');
      }
      
      if (data.success) {
        setResult({
          success: true,
          part_name: file.name.replace('.dxf', ''),
          gcode: data.gcode,
          operations: [],
          ai_notes: `Generated ${data.operations_count || 0} G-code moves from DXF using DerpCAM toolpath engine.`
        });
      } else {
        throw new Error(data.error || 'DXF processing failed');
      }
      
    } catch (err) {
      console.error('DXF processing error:', err);
      setError(err.message || 'Failed to process DXF file');
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Process STL file for turn/mill G-code
  const processStl = async () => {
    if (!file || inputMode !== 'stl') {
      setError('Please upload an STL file');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    setResult(null);
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const response = await fetch(`${API_URL}/api/stl/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stl_base64: base64,
          filename: file.name,
          material: stlSettings.material,
          units: stlSettings.units,
          user_tools: tools.length > 0 ? tools.map(t => ({
            tool_number: t.tool_number,
            description: t.description,
            tool_type: t.tool_type,
            nose_radius: t.nose_radius || null,
            tool_diameter: t.tool_diameter || null,
            insert_type: t.insert_type || null
          })) : []
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'STL processing failed');
      }
      
      if (data.success) {
        let aiNotes = `STL processed successfully.\n`;
        aiNotes += `Material: ${data.material}\n`;
        if (data.dimensions) {
          aiNotes += `Dimensions: OD ${data.dimensions.od?.toFixed(2)}mm x ${data.dimensions.length?.toFixed(2)}mm`;
          if (data.dimensions.bore > 0) {
            aiNotes += `, Bore ${data.dimensions.bore?.toFixed(2)}mm`;
          }
          aiNotes += '\n';
        }
        if (data.features_detected?.length > 0) {
          aiNotes += `\nFeatures detected:\n${data.features_detected.join('\n')}`;
        }
        
        setResult({
          success: true,
          part_name: data.part_name || file.name.replace('.stl', ''),
          material_detected: data.material,
          gcode: data.gcode,
          operations: data.operations || [],
          warnings: data.warnings || [],
          ai_notes: aiNotes
        });
      } else {
        throw new Error(data.error || 'STL processing failed');
      }
      
    } catch (err) {
      console.error('STL processing error:', err);
      setError(err.message || 'Failed to process STL file');
    } finally {
      setAnalyzing(false);
    }
  };

  // Process STEP file for CAD geometry extraction
  const processStep = async () => {
    if (!file || inputMode !== 'step') {
      setError('Please upload a STEP/STP file');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    setResult(null);
    
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const response = await fetch(`${API_URL}/api/step/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step_base64: base64,
          filename: file.name,
          machine_type: machineSetup.machine_type === 'lathe-turnmill' ? 'lathe' : machineSetup.machine_type,
          controller: machineSetup.controller,
          material: machineSetup.stock_material,
          units: stlSettings.units,
          user_tools: tools.length > 0 ? tools.map(t => ({
            tool_number: t.tool_number,
            description: t.description,
            tool_type: t.tool_type,
            nose_radius: t.nose_radius || null,
            tool_diameter: t.tool_diameter || null,
            insert_type: t.insert_type || null
          })) : []
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'STEP processing failed');
      }
      
      if (data.success) {
        let aiNotes = `STEP file processed successfully.\n`;
        aiNotes += `Part Name: ${data.part_name}\n`;
        aiNotes += `Machine: ${data.machine_type} / ${data.controller}\n`;
        
        if (data.bounding_box?.dimensions) {
          const dim = data.bounding_box.dimensions;
          aiNotes += `Bounding Box: ${dim.length?.toFixed(3)} x ${dim.width?.toFixed(3)} x ${dim.height?.toFixed(3)} ${data.units}\n`;
        }
        
        if (data.geometry) {
          aiNotes += `\nGeometry:\n`;
          aiNotes += `- Circles: ${data.geometry.circles?.length || 0}\n`;
          aiNotes += `- Cylinders: ${data.geometry.cylinders?.length || 0}\n`;
        }
        
        if (data.features?.length > 0) {
          aiNotes += `\nFeatures detected:\n`;
          data.features.forEach(f => {
            if (f.type === 'hole') {
              aiNotes += `- Hole: DIA ${f.diameter?.toFixed(4)}\n`;
            } else if (f.type === 'od_profile') {
              aiNotes += `- OD Profile: DIA ${f.diameter?.toFixed(4)} x ${f.length?.toFixed(4)}\n`;
            } else if (f.type === 'id_bore') {
              aiNotes += `- ID Bore: DIA ${f.diameter?.toFixed(4)} x ${f.depth?.toFixed(4)}\n`;
            }
          });
        }
        
        setResult({
          success: true,
          part_name: data.part_name || file.name.replace(/\.(stp|step)$/i, ''),
          material_detected: data.material,
          gcode: data.gcode,
          operations: data.features || [],
          warnings: [],
          ai_notes: aiNotes
        });
      } else {
        throw new Error(data.error || 'STEP processing failed');
      }
      
    } catch (err) {
      console.error('STEP processing error:', err);
      setError(err.message || 'Failed to process STEP file');
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeBlueprint = async () => {
    if (inputMode === 'file' && !file) {
      setError('Please upload a file first');
      return;
    }
    
    if (inputMode === 'manual' && features.length === 0) {
      setError('Please add at least one feature/dimension');
      return;
    }
    
    setAnalyzing(true);
    setError(null);
    setResult(null);
    
    try {
      let imageBase64 = null;
      
      // Convert file to base64 if using file mode
      if (inputMode === 'file' && file) {
        imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      // Build features data from manual entry
      const featureData = features.map(f => ({
        feature_type: f.feature_type,
        dimension: parseFloat(f.dimension),
        tolerance_plus: parseFloat(f.tolerance_plus),
        tolerance_minus: parseFloat(f.tolerance_minus),
        location: f.location,
        notes: f.notes + (f.surface_finish ? ` Surface: ${f.surface_finish}` : '')
      }));
      
      // Build additional notes with feature info if manual mode
      let notes = additionalNotes;
      if (inputMode === 'manual' && features.length > 0) {
        notes += '\n\nMANUAL DIMENSIONS PROVIDED:\n';
        features.forEach((f, i) => {
          const ft = featureTypes.find(t => t.value === f.feature_type);
          notes += `${i+1}. ${ft?.label || f.feature_type}: ${f.dimension}" (+${f.tolerance_plus}/-${f.tolerance_minus})`;
          if (f.location) notes += ` at ${f.location}`;
          if (f.surface_finish) notes += ` (${f.surface_finish} finish)`;
          notes += '\n';
        });
      }
      
      // Add tool info to notes
      if (tools.length > 0) {
        notes += '\n\nAVAILABLE TOOLS:\n';
        tools.forEach(t => {
          notes += `T${t.tool_number}: ${t.description} (${t.tool_type})`;
          if (t.nose_radius) notes += ` TNR=${t.nose_radius}"`;
          if (t.tool_diameter) notes += ` Dia=${t.tool_diameter}"`;
          notes += '\n';
        });
      }
      
      // Send analysis request
      // Use async job pattern to avoid proxy timeout
      const startResponse = await fetch(`${API_URL}/api/analyze-blueprint-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          machine_setup: machineSetup,
          image_base64: imageBase64 || '',
          additional_notes: notes,
          manual_features: featureData,
          generation_mode: generationMode,
          single_operation: generationMode === 'single_operation' ? singleOperation : null,
          ai_model: aiModel,
          user_tools: tools.length > 0 ? tools.map(t => ({
            tool_number: t.tool_number,
            description: t.description,
            tool_type: t.tool_type,
            nose_radius: t.nose_radius || null,
            tool_diameter: t.tool_diameter || null,
            insert_type: t.insert_type || null
          })) : []
        })
      });
      
      const startData = await startResponse.json();
      if (!startResponse.ok) throw new Error(startData.detail || 'Failed to start analysis');
      
      const jobId = startData.job_id;
      
      // Poll for results
      let attempts = 0;
      const maxAttempts = 120; // 4 minutes max (2s intervals)
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
        
        try {
          const statusResponse = await fetch(`${API_URL}/api/analyze-blueprint-status/${jobId}`);
          const statusData = await statusResponse.json();
          
          if (statusData.status === 'complete') {
            setResult(statusData.result);
            break;
          } else if (statusData.status === 'error') {
            throw new Error(statusData.error || 'Analysis failed');
          }
          // Still processing - update progress text if available
          if (statusData.progress) {
            setError(null); // Clear any previous errors
          }
        } catch (pollErr) {
          if (pollErr.message.includes('Analysis failed') || pollErr.message.includes('Budget')) throw pollErr;
          // Network glitch during poll - retry
        }
      }
      if (attempts >= maxAttempts && !result) {
        throw new Error('Analysis timed out. The AI is taking longer than expected. Please try again.');
      }
      
    } catch (err) {
      console.error('Analysis error:', err);
      const msg = err.message || 'Failed to analyze blueprint';
      if (msg.includes('Budget') || msg.includes('budget')) {
        setError('AI credit limit reached. Please top up your Emergent Universal Key balance (Profile → Universal Key → Add Balance) or add a Puter API token.');
      } else if (msg.includes('not configured')) {
        setError('AI service not configured. Add a Puter API token (free from puter.com/dashboard) or top up your Emergent Universal Key.');
      } else {
        setError(msg);
      }
    } finally {
      setAnalyzing(false);
    }
  };
  
  const copyCode = async () => {
    if (!result?.gcode) return;
    
    try {
      await navigator.clipboard.writeText(result.gcode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  const downloadCode = () => {
    if (!result?.gcode) return;
    
    const blob = new Blob([result.gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.part_name || 'program'}.nc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Save state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Save program to library
  const saveProgram = async () => {
    if (!result?.gcode) return;
    
    setSaving(true);
    try {
      const sourceMap = {
        manual: 'manual',
        file: 'ai-blueprint',
        dxf: 'dxf',
        stl: 'stl',
        step: 'step'
      };
      
      const programData = {
        name: result.part_name || `CAD_Import_${Date.now()}`,
        type: machineSetup.machine_type === 'mill' ? 'milling' : 'turning',
        controller: machineSetup.controller,
        gcode: result.gcode,
        description: `Generated from ${inputMode === 'stl' ? 'STL file' : inputMode === 'dxf' ? 'DXF file' : inputMode === 'step' ? 'STEP file' : inputMode === 'file' ? 'AI blueprint analysis' : 'manual entry'}`,
        source: sourceMap[inputMode] || inputMode,
        machine_setup: {
          machine_type: machineSetup.machine_type,
          controller: machineSetup.controller,
          workholding: machineSetup.workholding,
          stock_material: machineSetup.stock_material,
          stock_shape: machineSetup.stock_shape,
          stock_diameter: machineSetup.stock_diameter,
          stock_length: machineSetup.stock_length,
          part_quantity: machineSetup.part_quantity
        },
        tools_used: tools.length > 0 ? tools.map(t => ({
          tool_number: t.tool_number,
          description: t.description,
          tool_type: t.tool_type,
          insert_type: t.insert_type || null,
          nose_radius: t.nose_radius || null,
          tool_diameter: t.tool_diameter || null
        })) : null,
        setup_sheet: result.setup_sheet || null,
        tags: [
          machineSetup.machine_type,
          machineSetup.stock_material,
          sourceMap[inputMode] || inputMode
        ].filter(Boolean)
      };
      
      await axios.post(`${API_URL}/api/programs`, programData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving program:', error);
      alert('Failed to save program. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get current tool types based on machine
  const currentToolTypes = toolTypeOptions[machineSetup.machine_type] || toolTypeOptions.lathe;
  const currentToolType = currentToolTypes.find(t => t.value === newTool.tool_type) || currentToolTypes[0];

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <h2 className="font-heading font-bold text-blue-400 flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            AI G-CODE GENERATOR
          </h2>

          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 animate-in">
        {/* Input Mode Toggle */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <div className="text-xs text-industrial-text-secondary mb-2">Input Method</div>
          <div className="grid grid-cols-6 gap-2">
            <button
              onClick={() => setInputMode('manual')}
              data-testid="mode-manual"
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'manual'
                  ? 'bg-blue-500/20 border-blue-400 text-blue-400'
                  : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-blue-400'
              }`}
            >
              <Ruler className="w-4 h-4" />
              <span>Manual</span>
            </button>
            <button
              onClick={() => setInputMode('file')}
              data-testid="mode-file"
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'file'
                  ? 'bg-blue-500/20 border-blue-400 text-blue-400'
                  : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-blue-400'
              }`}
            >
              <Cpu className="w-4 h-4" />
              <span>AI 2D</span>
            </button>
            <button
              onClick={() => setInputMode('aichat')}
              data-testid="mode-aichat"
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'aichat'
                  ? 'bg-emerald-500/20 border-emerald-400 text-emerald-400'
                  : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-emerald-400'
              }`}
            >
              <Settings2 className="w-4 h-4" />
              <span>AI Chat</span>
            </button>
            <button
              onClick={() => setInputMode('dxf')}
              data-testid="mode-dxf"
              disabled={!derpcamAvailable}
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'dxf'
                  ? 'bg-green-500/20 border-green-400 text-green-400'
                  : derpcamAvailable
                    ? 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-green-400'
                    : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary/50 cursor-not-allowed'
              }`}
            >
              <FileCode className="w-4 h-4" />
              <span>DXF</span>
            </button>
            <button
              onClick={() => setInputMode('stl')}
              data-testid="mode-stl"
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'stl'
                  ? 'bg-orange-500/20 border-orange-400 text-orange-400'
                  : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-orange-400'
              }`}
            >
              <Box className="w-4 h-4" />
              <span>STL</span>
            </button>
            <button
              onClick={() => setInputMode('step')}
              data-testid="mode-step"
              className={`p-2 rounded-lg border text-center text-xs font-medium transition-all flex flex-col items-center justify-center gap-1 ${
                inputMode === 'step'
                  ? 'bg-purple-500/20 border-purple-400 text-purple-400'
                  : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-purple-400'
              }`}
            >
              <Box className="w-4 h-4" />
              <span>STEP</span>
            </button>
          </div>
          {inputMode === 'dxf' && (
            <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded text-xs text-green-400">
              DerpCAM: 2.5D toolpath generation from DXF files (pocket, contour, drill)
            </div>
          )}
          {inputMode === 'stl' && (
            <div className="mt-3 p-2 bg-orange-500/10 border border-orange-500/30 rounded text-xs text-orange-400">
              STL Processor: Automatic turn/mill program from 3D mesh geometry
            </div>
          )}
          {inputMode === 'step' && (
            <div className="mt-3 p-2 bg-purple-500/10 border border-purple-500/30 rounded text-xs text-purple-400">
              STEP/STP: CAD standard format - extracts geometry and generates G-code
            </div>
          )}
          {inputMode === 'aichat' && (
            <div className="mt-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
              Describe machining operations in plain language — AI generates G-code. Example: "Profile OD to 1.500, face, bore 0.750 ID 2" deep, thread 3/4-10 UNC"
            </div>
          )}
          
          {/* AI Generation Mode Selector - Show for AI, STL, STEP modes */}
          {(inputMode === 'file' || inputMode === 'stl' || inputMode === 'step' || inputMode === 'aichat') && (
            <div className="mt-3 bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-industrial-text">
                <Settings2 className="w-4 h-4 text-industrial-accent" />
                Generation Scope
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setGenerationMode('complete')}
                  data-testid="gen-mode-complete"
                  className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${
                    generationMode === 'complete'
                      ? 'bg-industrial-accent/20 border-industrial-accent text-industrial-accent'
                      : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-industrial-accent'
                  }`}
                >
                  Complete Program
                  <div className="text-[10px] mt-0.5 opacity-70">All Operations</div>
                </button>
                <button
                  onClick={() => setGenerationMode('single_operation')}
                  data-testid="gen-mode-single"
                  className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${
                    generationMode === 'single_operation'
                      ? 'bg-yellow-500/20 border-yellow-400 text-yellow-400'
                      : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-yellow-400'
                  }`}
                >
                  Single Operation
                  <div className="text-[10px] mt-0.5 opacity-70">Choose One</div>
                </button>
              </div>
              {generationMode === 'single_operation' && (
                <select
                  value={singleOperation}
                  onChange={(e) => setSingleOperation(e.target.value)}
                  data-testid="single-op-select"
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-sm text-industrial-text"
                >
                  <option value="">-- Select Operation --</option>
                  {singleOperationOptions.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>

        {/* AI Model Selector - Show for AI Blueprint mode */}
        {inputMode === 'file' && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-industrial-text">
              <Cpu className="w-4 h-4 text-purple-400" />
              AI Engine
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'claude-sonnet-4-5', label: 'Claude', sub: 'Sonnet 4.5' },
                { value: 'gpt-4o', label: 'GPT-4o', sub: 'OpenAI' },
                { value: 'deepseek-v4-flash', label: 'DeepSeek', sub: 'V4 Flash' },
              ].map(m => (
                <button
                  key={m.value}
                  onClick={() => setAiModel(m.value)}
                  data-testid={`ai-model-${m.value}`}
                  className={`p-2 rounded-lg border text-center text-xs font-medium transition-all ${
                    aiModel === m.value
                      ? 'bg-purple-500/20 border-purple-400 text-purple-400'
                      : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-purple-400/50'
                  }`}
                >
                  {m.label}
                  <div className="text-[10px] mt-0.5 opacity-70">{m.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* AI Chat Section */}
        {inputMode === 'aichat' && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-industrial-border flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-emerald-400" />
              <h3 className="font-heading font-semibold text-industrial-text text-sm">AI Machining Chat</h3>
            </div>
            <div className="p-4 space-y-3">
              {/* Chat Messages */}
              {chatMessages.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`p-2 rounded-lg text-sm ${msg.role === 'user' ? 'bg-blue-500/10 border border-blue-500/20 text-blue-300 ml-8' : 'bg-industrial-bg border border-industrial-border text-industrial-text mr-8'}`}>
                      <span className="text-[10px] text-industrial-text-secondary block mb-1">{msg.role === 'user' ? 'You' : 'AI'}</span>
                      <div className="whitespace-pre-wrap text-xs">{msg.content}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Input */}
              <div className="space-y-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Describe your machining operation...&#10;&#10;Examples:&#10;• Turn OD to 1.500 from 2.0 stock, face, bore 0.750 ID 1.5 deep&#10;• Profile the outside contour, pocket the inner hex, drill 4 bolt holes on 1.5 B.C.&#10;• G71 rough cycle from 2.0 stock to 1.250 finish dia, 3.0 long, 0.030x45 chamfer"
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-3 py-2 text-sm text-industrial-text placeholder:text-industrial-text-secondary resize-none"
                  rows={4}
                  data-testid="ai-chat-input"
                />
                <button
                  onClick={async () => {
                    if (!chatInput.trim() || chatLoading) return;
                    const userMsg = chatInput.trim();
                    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
                    setChatInput('');
                    setChatLoading(true);
                    try {
                      // Use the existing AI analysis pipeline with the chat message as additional_notes
                      const res = await axios.post(`${API_URL}/api/analyze-blueprint-async`, {
                        machine_setup: {
                          machine_type: machineSetup.machine_type,
                          controller: machineSetup.controller,
                          workholding: machineSetup.workholding,
                          stock_material: machineSetup.stock_material,
                          stock_shape: machineSetup.stock_shape,
                          stock_diameter: parseFloat(machineSetup.stock_diameter) || 2.0,
                          stock_length: parseFloat(machineSetup.stock_length) || 4.0,
                        },
                        image_base64: '',
                        additional_notes: `GENERATE G-CODE FROM DESCRIPTION: ${userMsg}`,
                        ai_model: aiModel,
                        generation_mode: generationMode,
                        user_tools: tools.length > 0 ? tools.map(t => ({
                          tool_number: t.tool_number,
                          description: t.description,
                          tool_type: t.tool_type,
                          nose_radius: t.nose_radius || null,
                          tool_diameter: t.tool_diameter || null,
                          insert_type: t.insert_type || null
                        })) : [],
                      });
                      const jobId = res.data.job_id;
                      // Poll for result
                      const poll = async () => {
                        const status = await axios.get(`${API_URL}/api/analyze-blueprint-status/${jobId}`);
                        if (status.data.status === 'complete') {
                          const r = status.data.result;
                          setChatMessages(prev => [...prev, { role: 'assistant', content: r.gcode ? `G-code generated (${r.gcode.split('\n').length} lines). Check the output below.` : 'AI analysis complete but no G-code generated. Try being more specific about dimensions.' }]);
                          setResult(r);
                          setChatLoading(false);
                        } else if (status.data.status === 'error') {
                          setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${status.data.error}` }]);
                          setChatLoading(false);
                        } else {
                          setTimeout(poll, 2000);
                        }
                      };
                      setTimeout(poll, 2000);
                    } catch (err) {
                      setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
                      setChatLoading(false);
                    }
                  }}
                  disabled={!chatInput.trim() || chatLoading}
                  className="w-full py-2.5 bg-emerald-500 text-white rounded-lg font-medium text-sm disabled:opacity-40 hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
                  data-testid="ai-chat-send"
                >
                  {chatLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                  ) : (
                    <><Cpu className="w-4 h-4" /> Generate G-Code</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Machine Setup Section */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <button
            onClick={() => setShowSetup(!showSetup)}
            className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
            data-testid="setup-toggle"
          >
            <div className="flex items-center gap-2">
              <Settings2 className="w-4 h-4 text-industrial-readout" />
              <h3 className="font-heading font-semibold text-industrial-text">Machine Setup</h3>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showSetup ? 'rotate-180' : ''}`} />
          </button>
          
          {showSetup && (
            <div className="p-4 pt-0 space-y-4 border-t border-industrial-border">
              {/* Machine Type */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Machine Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'lathe', label: 'Lathe' },
                    { value: 'mill', label: 'Mill' },
                    { value: 'lathe-turnmill', label: 'Turn-Mill' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => updateMachineSetup('machine_type', type.value)}
                      data-testid={`machine-${type.value}`}
                      className={`p-3 rounded-lg border text-center text-sm font-medium transition-all ${
                        machineSetup.machine_type === type.value
                          ? 'bg-blue-500/20 border-blue-400 text-blue-400'
                          : 'bg-industrial-bg border-industrial-border text-industrial-text-secondary hover:border-blue-400'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Controller */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Controller</label>
                <select
                  value={machineSetup.controller}
                  onChange={(e) => updateMachineSetup('controller', e.target.value)}
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-4 py-3 text-industrial-text"
                  data-testid="controller-select"
                >
                  {Object.entries(controllerConfig).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Workholding */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Workholding</label>
                <select
                  value={machineSetup.workholding}
                  onChange={(e) => updateMachineSetup('workholding', e.target.value)}
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-4 py-3 text-industrial-text"
                  data-testid="workholding-select"
                >
                  {workholdingOptions[machineSetup.machine_type]?.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Material */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Stock Material</label>
                <select
                  value={machineSetup.stock_material}
                  onChange={(e) => updateMachineSetup('stock_material', e.target.value)}
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-4 py-3 text-industrial-text"
                  data-testid="material-select"
                >
                  {Object.entries(materialLibrary).map(([key, mat]) => (
                    <option key={key} value={key}>{mat.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Stock Shape */}
              <div>
                <label className="text-xs text-industrial-text-secondary mb-2 block">Stock Shape</label>
                <select
                  value={machineSetup.stock_shape}
                  onChange={(e) => updateMachineSetup('stock_shape', e.target.value)}
                  className="w-full bg-industrial-bg border border-industrial-border rounded-lg px-4 py-3 text-industrial-text"
                  data-testid="shape-select"
                >
                  {stockShapeOptions[machineSetup.machine_type]?.map(shape => (
                    <option key={shape.value} value={shape.value}>{shape.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Stock Dimensions */}
              <div className="grid grid-cols-2 gap-3">
                {(machineSetup.stock_shape === 'round-bar' || machineSetup.stock_shape === 'tube' || 
                  machineSetup.stock_shape === 'hex-bar' || machineSetup.stock_shape === 'casting') && (
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">
                      {machineSetup.stock_shape === 'hex-bar' ? 'Across Flats (in)' : 'Stock Diameter (in)'}
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={machineSetup.stock_diameter || ''}
                      onChange={(e) => updateMachineSetup('stock_diameter', parseFloat(e.target.value) || null)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                      data-testid="stock-diameter"
                    />
                  </div>
                )}
                
                {(machineSetup.stock_shape === 'plate' || machineSetup.stock_shape === 'block' || 
                  machineSetup.stock_shape === 'flat-steel') && (
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Width (in)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={machineSetup.stock_width || ''}
                      onChange={(e) => updateMachineSetup('stock_width', parseFloat(e.target.value) || null)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Length (in)</label>
                  <input
                    type="number"
                    step="0.001"
                    value={machineSetup.stock_length || ''}
                    onChange={(e) => updateMachineSetup('stock_length', parseFloat(e.target.value) || null)}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="stock-length"
                  />
                </div>
                
                {(machineSetup.stock_shape === 'plate' || machineSetup.stock_shape === 'block' || 
                  machineSetup.stock_shape === 'flat-steel') && (
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Thickness/Height (in)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={machineSetup.stock_height || ''}
                      onChange={(e) => updateMachineSetup('stock_height', parseFloat(e.target.value) || null)}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    />
                  </div>
                )}
              </div>
              
              {/* Bar Pull Length */}
              {(machineSetup.machine_type === 'lathe' || machineSetup.machine_type === 'lathe-turnmill') && (
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">
                    Bar Pull Length (in) - For multiple parts from bar stock
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={machineSetup.bar_pull_length || ''}
                    onChange={(e) => updateMachineSetup('bar_pull_length', parseFloat(e.target.value) || null)}
                    placeholder="Leave blank for single part"
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                    data-testid="bar-pull-length"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Manual Feature Entry Section */}
        {inputMode === 'manual' && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
            <button
              onClick={() => setShowFeatures(!showFeatures)}
              className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
            >
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-industrial-success" />
                <h3 className="font-heading font-semibold text-industrial-text">Part Dimensions & Tolerances</h3>
                <span className="text-xs text-industrial-text-secondary">({features.length} features)</span>
              </div>
              <ChevronDown className={`w-5 h-5 transition-transform ${showFeatures ? 'rotate-180' : ''}`} />
            </button>
            
            {showFeatures && (
              <div className="p-4 pt-0 space-y-4 border-t border-industrial-border">
                {/* Existing Features */}
                {features.length > 0 && (
                  <div className="space-y-2">
                    {features.map((feat, i) => {
                      const ft = featureTypes.find(t => t.value === feat.feature_type);
                      return (
                        <div key={feat.id} className="flex items-center justify-between p-3 bg-industrial-bg rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-industrial-success/20 text-industrial-success text-xs flex items-center justify-center font-bold">
                              {i + 1}
                            </span>
                            <div>
                              <span className="text-industrial-text font-medium">{ft?.label || feat.feature_type}</span>
                              <span className="text-industrial-readout font-mono ml-2">{feat.dimension}"</span>
                              <span className="text-xs text-industrial-text-secondary ml-2">
                                (+{feat.tolerance_plus}/-{feat.tolerance_minus})
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => removeFeature(feat.id)}
                            className="p-1 text-industrial-danger hover:bg-industrial-danger/20 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Add New Feature */}
                <div className="p-3 bg-industrial-bg/50 rounded-lg space-y-3">
                  <div className="text-xs text-industrial-text-secondary font-medium flex items-center gap-2">
                    <CircleDot className="w-3 h-3" />
                    Add Dimension/Feature
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Feature Type</label>
                      <select
                        value={newFeature.feature_type}
                        onChange={(e) => setNewFeature({ ...newFeature, feature_type: e.target.value })}
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                        data-testid="feature-type"
                      >
                        {featureTypes.map(ft => (
                          <option key={ft.value} value={ft.value}>{ft.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Dimension (in)</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={newFeature.dimension}
                        onChange={(e) => setNewFeature({ ...newFeature, dimension: e.target.value })}
                        placeholder="e.g., 1.500"
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                        data-testid="feature-dimension"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Tol + (in)</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={newFeature.tolerance_plus}
                        onChange={(e) => setNewFeature({ ...newFeature, tolerance_plus: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Tol - (in)</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={newFeature.tolerance_minus}
                        onChange={(e) => setNewFeature({ ...newFeature, tolerance_minus: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Location</label>
                      <input
                        type="text"
                        value={newFeature.location}
                        onChange={(e) => setNewFeature({ ...newFeature, location: e.target.value })}
                        placeholder="e.g., Z-1.0"
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Surface Finish</label>
                      <select
                        value={newFeature.surface_finish}
                        onChange={(e) => setNewFeature({ ...newFeature, surface_finish: e.target.value })}
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                      >
                        <option value="">Not specified</option>
                        <option value="125Ra">125 Ra (Standard)</option>
                        <option value="63Ra">63 Ra (Fine)</option>
                        <option value="32Ra">32 Ra (Very Fine)</option>
                        <option value="16Ra">16 Ra (Ground)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">Notes</label>
                      <input
                        type="text"
                        value={newFeature.notes}
                        onChange={(e) => setNewFeature({ ...newFeature, notes: e.target.value })}
                        placeholder="e.g., thread pitch, chamfer angle"
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={addFeature}
                    data-testid="add-feature-btn"
                    className="w-full py-2 bg-industrial-success/20 text-industrial-success rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-industrial-success/30 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Feature
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tool Library Section */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <button
            onClick={() => setShowToolLibrary(!showToolLibrary)}
            className="w-full flex items-center justify-between p-4 hover:bg-industrial-surface-highlight transition-colors"
          >
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-industrial-warning" />
              <h3 className="font-heading font-semibold text-industrial-text">Tool Library</h3>
              <span className="text-xs text-industrial-text-secondary">({tools.length} tools)</span>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${showToolLibrary ? 'rotate-180' : ''}`} />
          </button>
          
          {showToolLibrary && (
            <div className="p-4 pt-0 space-y-4 border-t border-industrial-border">
              {/* Quick Select from Pre-defined Library */}
              <div className="bg-industrial-bg/50 rounded-lg p-3">
                <div className="text-xs text-industrial-text-secondary font-medium mb-2">Quick Select Pre-defined Tools</div>
                <div className="grid grid-cols-2 gap-2">
                  {machineSetup.machine_type === 'lathe' || machineSetup.machine_type === 'lathe-turnmill' ? (
                    <>
                      <button
                        onClick={() => addPresetTool('od-rough', 'T01', 'OD Roughing CNMG-432', 'CNMG', 0.031)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T01</span>
                        <span className="text-industrial-text ml-1">OD Rough</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('od-finish', 'T02', 'OD Finishing DNMG-332', 'DNMG', 0.016)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T02</span>
                        <span className="text-industrial-text ml-1">OD Finish</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('id-boring', 'T03', 'ID Boring Bar CCMT-21.51', 'CCMT', 0.008)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T03</span>
                        <span className="text-industrial-text ml-1">ID Bore</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('facing', 'T04', 'Facing Tool SNMG-432', 'SNMG', 0.031)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T04</span>
                        <span className="text-industrial-text ml-1">Facing</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('grooving', 'T05', 'Grooving Tool 0.125W', 'GIP', null)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T05</span>
                        <span className="text-industrial-text ml-1">Grooving</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('parting', 'T06', 'Part-off Tool 0.093W', 'GIP', null)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T06</span>
                        <span className="text-industrial-text ml-1">Part-off</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('threading', 'T07', '60DEG Threading UN', '60DEG', null)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T07</span>
                        <span className="text-industrial-text ml-1">Threading</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('center-drill', 'T08', 'Center Drill #3', 'CD', null)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T08</span>
                        <span className="text-industrial-text ml-1">Center Drill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('bar-puller', 'T09', '1" Sq Heavy Duty Bar Puller', 'BP', null)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T09</span>
                        <span className="text-industrial-text ml-1">Bar Puller</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => addPresetTool('endmill', 'T01', '0.500 4FL Carbide Endmill', 'CARB', null, 0.500)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T01</span>
                        <span className="text-industrial-text ml-1">1/2" End Mill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('endmill', 'T02', '0.250 4FL Carbide Endmill', 'CARB', null, 0.250)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T02</span>
                        <span className="text-industrial-text ml-1">1/4" End Mill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('drill', 'T03', '0.250 Carbide Drill', 'CARB', null, 0.250)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T03</span>
                        <span className="text-industrial-text ml-1">1/4" Drill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('drill', 'T04', '0.500 Carbide Drill', 'CARB', null, 0.500)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T04</span>
                        <span className="text-industrial-text ml-1">1/2" Drill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('facemill', 'T05', '2.000 Face Mill', 'INS', null, 2.000)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T05</span>
                        <span className="text-industrial-text ml-1">2" Face Mill</span>
                      </button>
                      <button
                        onClick={() => addPresetTool('chamfer', 'T06', '90DEG Chamfer Mill', 'CARB', null, 0.500)}
                        className="text-xs p-2 bg-industrial-surface border border-industrial-border rounded hover:border-industrial-warning transition-colors text-left"
                      >
                        <span className="text-industrial-readout font-mono">T06</span>
                        <span className="text-industrial-text ml-1">Chamfer 90°</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Existing Tools */}
              {tools.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-industrial-text-secondary font-medium">Current Tool List</div>
                  {tools.map(tool => (
                    <div key={tool.id} className="flex items-center justify-between p-3 bg-industrial-bg rounded-lg">
                      <div>
                        <span className="text-industrial-readout font-mono">T{String(tool.tool_number).padStart(2, '0')}</span>
                        <span className="text-industrial-text ml-2">{tool.description}</span>
                        <span className="text-xs text-industrial-text-secondary ml-2">
                          ({tool.tool_type} - {tool.insert_type})
                        </span>
                        {tool.nose_radius && (
                          <span className="text-xs text-industrial-readout ml-1">TNR={tool.nose_radius}"</span>
                        )}
                        {tool.tool_diameter && (
                          <span className="text-xs text-industrial-readout ml-1">D={tool.tool_diameter}"</span>
                        )}
                      </div>
                      <button
                        onClick={() => removeTool(tool.id)}
                        className="p-1 text-industrial-danger hover:bg-industrial-danger/20 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Tool */}
              <div className="p-3 bg-industrial-bg/50 rounded-lg space-y-3">
                <div className="text-xs text-industrial-text-secondary font-medium">Add Tool</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Tool #</label>
                    <input
                      type="number"
                      value={newTool.tool_number}
                      onChange={(e) => setNewTool({ ...newTool, tool_number: parseInt(e.target.value) || 1 })}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Type</label>
                    <select
                      value={newTool.tool_type}
                      onChange={(e) => {
                        const tt = currentToolTypes.find(t => t.value === e.target.value);
                        setNewTool({ 
                          ...newTool, 
                          tool_type: e.target.value,
                          insert_type: tt?.inserts[0] || ''
                        });
                      }}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                    >
                      {currentToolTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Insert</label>
                    <select
                      value={newTool.insert_type}
                      onChange={(e) => setNewTool({ ...newTool, insert_type: e.target.value })}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                    >
                      {currentToolType.inserts.map(ins => (
                        <option key={ins} value={ins}>{ins}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Description</label>
                  <input
                    type="text"
                    value={newTool.description}
                    onChange={(e) => setNewTool({ ...newTool, description: e.target.value })}
                    placeholder="e.g., 80DEG DIAMOND CNMG-432"
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Nose R (in)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={newTool.nose_radius || ''}
                      onChange={(e) => setNewTool({ ...newTool, nose_radius: parseFloat(e.target.value) || null })}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Diameter (in)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={newTool.tool_diameter || ''}
                      onChange={(e) => setNewTool({ ...newTool, tool_diameter: parseFloat(e.target.value) || null })}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-industrial-text-secondary mb-1 block">Max DOC (in)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={newTool.max_doc || ''}
                      onChange={(e) => setNewTool({ ...newTool, max_doc: parseFloat(e.target.value) || null })}
                      className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
                    />
                  </div>
                </div>
                <button
                  onClick={addTool}
                  className="w-full py-2 bg-industrial-warning/20 text-industrial-warning rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-industrial-warning/30 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Tool
                </button>
              </div>
            </div>
          )}
        </div>

        {/* DXF Tool Settings (only in dxf mode) */}
        {inputMode === 'dxf' && (
          <div className="bg-industrial-surface border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cog className="w-4 h-4 text-green-400" />
              <h3 className="font-heading font-semibold text-green-400">DXF Toolpath Settings</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Tool Diameter (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={dxfTool.diameter}
                  onChange={(e) => setDxfTool({ ...dxfTool, diameter: parseFloat(e.target.value) || 0.25 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  data-testid="dxf-tool-diameter"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Material Depth (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={dxfTool.depth}
                  onChange={(e) => setDxfTool({ ...dxfTool, depth: parseFloat(e.target.value) || 0.25 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  data-testid="dxf-material-depth"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Horiz. Feed (IPM)</label>
                <input
                  type="number"
                  step="0.1"
                  value={dxfTool.hfeed}
                  onChange={(e) => setDxfTool({ ...dxfTool, hfeed: parseFloat(e.target.value) || 20 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Vert. Feed (IPM)</label>
                <input
                  type="number"
                  step="0.1"
                  value={dxfTool.vfeed}
                  onChange={(e) => setDxfTool({ ...dxfTool, vfeed: parseFloat(e.target.value) || 5 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-industrial-text-secondary mb-1 block">Max Depth per Pass (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={dxfTool.maxdoc}
                  onChange={(e) => setDxfTool({ ...dxfTool, maxdoc: parseFloat(e.target.value) || 0.05 })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>
            <div className="mt-3 p-2 bg-industrial-bg rounded text-xs text-industrial-text-secondary">
              <strong>DXF Line Type Mapping:</strong><br/>
              • CONTINUOUS → Outside Contour<br/>
              • DASHED/DOT → Inside Contour<br/>
              • HIDDEN/BORDER → Pocket<br/>
              • Circles → Helical Bore/Drill
            </div>
          </div>
        )}

        {/* STL Settings Section (only in STL mode) */}
        {inputMode === 'stl' && (
          <div className="bg-industrial-surface border border-orange-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Cog className="w-4 h-4 text-orange-400" />
              <h3 className="font-heading font-semibold text-orange-400">STL Processing Settings</h3>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Material</label>
                <select
                  value={stlSettings.material}
                  onChange={(e) => setStlSettings({ ...stlSettings, material: e.target.value })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                  data-testid="stl-material"
                >
                  {STL_MATERIALS.map(m => (
                    <option key={m.key} value={m.key}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Output Units</label>
                <select
                  value={stlSettings.units}
                  onChange={(e) => setStlSettings({ ...stlSettings, units: e.target.value })}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
                  data-testid="stl-units"
                >
                  <option value="mm">Metric (mm)</option>
                  <option value="inch">Imperial (inch)</option>
                </select>
              </div>
            </div>
            <div className="mt-3 p-2 bg-industrial-bg rounded text-xs text-industrial-text-secondary">
              <strong>STL Processor Features:</strong><br/>
              • Automatic axis detection<br/>
              • OD/ID profile extraction<br/>
              • Teeth/slot detection for C-axis milling<br/>
              • Complete FANUC turn/mill program generation
            </div>
          </div>
        )}

        {/* File Upload Area (for file, dxf, stl, and step modes) */}
        {(inputMode === 'file' || inputMode === 'dxf' || inputMode === 'stl' || inputMode === 'step') && (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
              dragActive
                ? inputMode === 'stl' ? 'border-orange-400 bg-orange-400/10' 
                : inputMode === 'step' ? 'border-purple-400 bg-purple-400/10'
                : 'border-blue-400 bg-blue-400/10'
                : 'border-industrial-border hover:border-industrial-text-secondary'
            }`}
          >
            <input
              type="file"
              accept={inputMode === 'stl' ? '.stl' : inputMode === 'dxf' ? '.dxf' : inputMode === 'step' ? '.stp,.step' : '.pdf,.jpg,.jpeg,.png,.dxf,.dwg,.stl,.stp,.step'}
              onChange={handleFileInput}
              className="hidden"
              id="file-input"
              data-testid="file-input"
            />
            
            {file ? (
              <div>
                {filePreview ? (
                  <img 
                    src={filePreview} 
                    alt="Blueprint preview" 
                    className="max-h-48 mx-auto mb-4 rounded-lg border border-industrial-border"
                  />
                ) : (
                  inputMode === 'stl' || inputMode === 'step' ? (
                    <Box className={`w-16 h-16 mx-auto mb-4 ${inputMode === 'step' ? 'text-purple-400' : 'text-orange-400'}`} />
                  ) : (
                    <FileText className="w-16 h-16 mx-auto text-blue-400 mb-4" />
                  )
                )}
                <p className="font-medium text-industrial-text mb-1">{file.name}</p>
                <p className="text-sm text-industrial-text-secondary mb-4">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={() => { setFile(null); setFilePreview(null); setResult(null); }}
                  className="text-industrial-danger text-sm hover:underline"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <label htmlFor="file-input" className="cursor-pointer block">
                {inputMode === 'stl' || inputMode === 'step' ? (
                  <Box className={`w-16 h-16 mx-auto mb-4 ${inputMode === 'step' ? 'text-purple-400/50' : 'text-industrial-text-secondary'}`} />
                ) : (
                  <Upload className="w-16 h-16 mx-auto text-industrial-text-secondary mb-4" />
                )}
                <p className="font-medium text-industrial-text mb-1">
                  {inputMode === 'stl' 
                    ? 'Drop STL file here or tap to upload' 
                    : inputMode === 'step'
                      ? 'Drop STEP/STP file here or tap to upload'
                      : 'Drop blueprint here or tap to upload'}
                </p>
                <p className="text-sm text-industrial-text-secondary">
                  {inputMode === 'stl' 
                    ? 'Supports ASCII and Binary STL files' 
                    : inputMode === 'step'
                      ? 'Supports STEP/STP CAD files'
                      : inputMode === 'dxf' 
                        ? 'Supports DXF files' 
                        : 'Supports PDF, JPG, PNG, DXF, DWG, STL, STEP'}
                </p>
              </label>
            )}
          </div>
        )}
        
        {/* Additional Notes */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <label className="text-xs text-industrial-text-secondary mb-2 block">
            Additional Machining Notes (optional)
          </label>
          <textarea
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder="Special requirements: surface finish specs, chamfer details, thread pitch, deburr edges, etc."
            rows={3}
            className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text text-sm resize-none"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-industrial-danger/10 border border-industrial-danger/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-industrial-danger">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Generate Button */}
        {!result && (
          <button
            onClick={inputMode === 'dxf' ? processDxf : inputMode === 'stl' ? processStl : inputMode === 'step' ? processStep : analyzeBlueprint}
            disabled={analyzing || 
              ((inputMode === 'file' || inputMode === 'dxf' || inputMode === 'stl' || inputMode === 'step') && !file) || 
              (inputMode === 'manual' && features.length === 0)}
            data-testid="generate-btn"
            className={`w-full py-4 ${
              inputMode === 'dxf' ? 'bg-green-500' : 
              inputMode === 'stl' ? 'bg-orange-500' : 
              inputMode === 'step' ? 'bg-purple-500' :
              'bg-blue-500'
            } text-white font-heading font-bold uppercase tracking-wider rounded-lg shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {inputMode === 'dxf' ? 'Processing DXF...' : 
                 inputMode === 'stl' ? 'Processing STL...' : 
                 inputMode === 'step' ? 'Processing STEP...' :
                 'Generating G-Code...'}
              </>
            ) : (
              <>
                {inputMode === 'dxf' ? <FileCode className="w-5 h-5" /> : 
                 inputMode === 'stl' || inputMode === 'step' ? <Box className="w-5 h-5" /> : 
                 <Cpu className="w-5 h-5" />}
                {inputMode === 'dxf' ? 'Generate Toolpaths from DXF' : 
                 inputMode === 'stl' ? 'Generate Turn/Mill Program from STL' : 
                 inputMode === 'step' ? 'Generate G-Code from STEP' :
                 'Generate G-Code Program'}
              </>
            )}
          </button>
        )}

        {/* Results Section */}
        {result && (
          <div className="space-y-4 animate-in">
            {/* Success Header */}
            {result.gcode ? (
              <div className="bg-industrial-success/10 border border-industrial-success/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-industrial-success mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">G-Code Generated Successfully</span>
                </div>
                {result.part_name && (
                  <p className="text-industrial-text">Part: <span className="font-mono">{result.part_name}</span></p>
                )}
              </div>
            ) : (
              <div className="bg-industrial-warning/10 border border-industrial-warning/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-industrial-warning mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Analysis Complete - No G-Code Generated</span>
                </div>
                <p className="text-sm text-industrial-text-secondary">
                  The AI analyzed the image but could not generate a complete program. Try uploading a clearer image or add dimensions manually.
                </p>
              </div>
            )}
            
            {/* Warnings */}
            {result.warnings?.length > 0 && (
              <div className="bg-industrial-warning/10 border border-industrial-warning/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-industrial-warning mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium text-sm">Warnings</span>
                </div>
                <ul className="text-sm text-industrial-text-secondary space-y-1">
                  {result.warnings.map((w, i) => (
                    <li key={i}>• {w}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Operations Sequence */}
            {result.operations?.length > 0 && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
                <h4 className="font-heading font-semibold text-industrial-text mb-3">Operation Sequence</h4>
                <div className="space-y-2">
                  {result.operations.map((op, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-industrial-bg rounded">
                      <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs flex items-center justify-center font-bold">
                        {op.op_num || i + 1}
                      </span>
                      <div className="flex-1">
                        <span className="text-industrial-text font-medium">{op.operation}</span>
                        <span className="text-industrial-text-secondary text-xs ml-2">T{op.tool}</span>
                      </div>
                      <span className="text-xs text-industrial-text-secondary">
                        S{op.speed} F{op.feed}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Generated G-Code */}
            {result.gcode && (
              <>
                {/* 3D Toolpath Simulation */}
                <ToolpathViewer3D
                  gcode={result.gcode}
                  machineType={machineSetup.machine_type === 'mill' ? 'milling' : 'turning'}
                  stockDimensions={machineSetup.machine_type !== 'mill'
                    ? { diameter: parseFloat(machineSetup.stock_diameter) || 2.0, length: parseFloat(machineSetup.stock_length) || 4.0 }
                    : { width: 6, height: 2, length: 4 }
                  }
                  showRapids={true}
                  showWorkpiece={true}
                  height={350}
                />
                
                <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border">
                  <span className="text-sm font-medium text-industrial-text">Generated G-Code</span>
                  <div className="flex gap-2">
                    <button
                      onClick={saveProgram}
                      disabled={saving || saved}
                      data-testid="save-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors disabled:opacity-50"
                      title="Save to Library"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 text-industrial-text-secondary animate-spin" />
                      ) : saved ? (
                        <CheckCircle className="w-4 h-4 text-industrial-success" />
                      ) : (
                        <Save className="w-4 h-4 text-industrial-text-secondary" />
                      )}
                    </button>
                    <button
                      onClick={copyCode}
                      data-testid="copy-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                      title="Copy to Clipboard"
                    >
                      <Copy className={`w-4 h-4 ${copied ? 'text-industrial-success' : 'text-industrial-text-secondary'}`} />
                    </button>
                    <button
                      onClick={downloadCode}
                      data-testid="download-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                      title="Download .nc file"
                    >
                      <Download className="w-4 h-4 text-industrial-text-secondary" />
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-96 overflow-y-auto bg-[#1e1e1e]">
                  <pre className="font-mono text-sm leading-relaxed text-green-400 whitespace-pre-wrap">
                    {result.gcode}
                  </pre>
                </div>
              </div>
              </>
            )}
            
            {/* AI Notes */}
            {result.ai_notes && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
                <h4 className="font-heading font-semibold text-industrial-text mb-2">Drawing Analysis</h4>
                <div className="text-sm text-industrial-text-secondary whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">{result.ai_notes}</div>
              </div>
            )}

            {/* Extracted Dimensions - Editable for Override */}
            {result.features?.length > 0 && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4" data-testid="extracted-dimensions">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-heading font-semibold text-industrial-text">Extracted Dimensions ({result.features.length})</h4>
                  <span className="text-[10px] text-industrial-text-secondary bg-industrial-bg px-2 py-1 rounded">Click any value to override</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {result.features.map((f, i) => {
                    const typeColors = {
                      'od-diameter': 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
                      'id-diameter': 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
                      'od-profile-step': 'text-green-400 bg-green-500/10 border-green-500/20',
                      'length': 'text-blue-400 bg-blue-500/10 border-blue-500/20',
                      'thread': 'text-orange-400 bg-orange-500/10 border-orange-500/20',
                      'groove': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
                      'chamfer': 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
                      'radius': 'text-pink-400 bg-pink-500/10 border-pink-500/20',
                      'cross-hole': 'text-red-400 bg-red-500/10 border-red-500/20',
                      'bolt-circle': 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
                      'milled-feature': 'text-amber-400 bg-amber-500/10 border-amber-500/20',
                      'hole-pattern': 'text-teal-400 bg-teal-500/10 border-teal-500/20',
                    };
                    const typeLabels = {
                      'od-diameter': 'OD', 'id-diameter': 'ID', 'od-profile-step': 'Profile',
                      'length': 'Length', 'thread': 'Thread', 'groove': 'Groove',
                      'chamfer': 'Chamfer', 'radius': 'Radius',
                      'cross-hole': 'Cross Hole', 'bolt-circle': 'Bolt Circle',
                      'milled-feature': 'Mill Feature', 'hole-pattern': 'Pattern',
                    };
                    const color = typeColors[f.feature_type] || 'text-gray-400 bg-gray-500/10 border-gray-500/20';
                    const isEditing = editingFeatureIdx === i;
                    return (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded border ${color} ${f.overridden ? 'ring-1 ring-industrial-safety' : ''}`} data-testid={`feature-${i}`}>
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-black/20 flex-shrink-0">{typeLabels[f.feature_type] || f.feature_type}</span>
                        {isEditing ? (
                          <input
                            type="text"
                            autoFocus
                            defaultValue={f.dimension != null ? f.dimension : f.notes}
                            onBlur={(e) => {
                              const val = e.target.value.trim();
                              const newFeatures = [...result.features];
                              // Strict numeric check: only pure numbers like "1.500" or "0.031"
                              const isNumeric = /^-?\d+\.?\d*$/.test(val);
                              if (isNumeric) {
                                newFeatures[i] = { ...f, dimension: parseFloat(val), overridden: true };
                              } else {
                                newFeatures[i] = { ...f, notes: val, dimension: null, overridden: true };
                              }
                              setResult({ ...result, features: newFeatures });
                              setEditingFeatureIdx(null);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFeatureIdx(null); }}
                            className="flex-1 bg-industrial-bg border border-industrial-safety rounded px-2 py-0.5 text-xs font-mono text-industrial-text outline-none"
                            data-testid={`feature-edit-${i}`}
                          />
                        ) : (
                          <span
                            onClick={() => setEditingFeatureIdx(i)}
                            className="font-mono text-sm flex-1 truncate cursor-pointer hover:underline"
                            title="Click to override"
                          >
                            {f.dimension != null ? (f.feature_type === 'hole-pattern' ? `${f.dimension}x` : `${f.dimension}"`) : ''} {f.notes}
                            {f.overridden && <span className="ml-1 text-[9px] text-industrial-safety">(edited)</span>}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {result.features?.some(f => f.overridden) && (
                  <div className="mt-3 p-3 bg-industrial-safety/10 border border-industrial-safety/20 rounded-lg">
                    <p className="text-xs text-industrial-text-secondary mb-2">Dimensions were edited. Regenerate G-code with your corrections:</p>
                    <button
                      onClick={() => {
                        const overrides = result.features
                          .filter(f => f.overridden)
                          .map(f => `${f.feature_type}: ${f.dimension != null ? f.dimension : f.notes}`)
                          .join(', ');
                        const existingNotes = document.querySelector('[data-testid="additional-notes"]');
                        if (existingNotes) {
                          existingNotes.value = `DIMENSION CORRECTIONS: ${overrides}. ` + (existingNotes.value || '');
                          existingNotes.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full py-2.5 bg-industrial-safety text-black rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-industrial-safety/90 transition-colors"
                      data-testid="regenerate-with-overrides-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Apply Overrides & Regenerate
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Setup Sheet */}
            {result.setup_sheet && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden" data-testid="setup-sheet">
                <div className="px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-industrial-readout" />
                  <span className="text-sm font-medium text-industrial-text">Setup Sheet</span>
                </div>
                <div className="p-4 space-y-4">
                  {/* Work Coordinates */}
                  <div>
                    <h5 className="text-xs font-semibold text-industrial-text-secondary uppercase mb-2">Work Coordinates</h5>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="p-2 bg-industrial-bg rounded border border-industrial-border">
                        <span className="text-industrial-text-secondary">Z0: </span>
                        <span className="text-industrial-readout font-mono">{result.setup_sheet.work_zero_z || 'Face of part'}</span>
                      </div>
                      <div className="p-2 bg-industrial-bg rounded border border-industrial-border">
                        <span className="text-industrial-text-secondary">X0: </span>
                        <span className="text-industrial-readout font-mono">{result.setup_sheet.work_zero_x || 'Centerline'}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tool Offset Table */}
                  {result.setup_sheet.tools && result.setup_sheet.tools.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-industrial-text-secondary uppercase mb-2">Tool Offsets</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-industrial-border text-industrial-text-secondary">
                              <th className="text-left p-1.5">Station</th>
                              <th className="text-left p-1.5">Description</th>
                              <th className="text-left p-1.5">Insert</th>
                              <th className="text-right p-1.5">Nose R</th>
                              <th className="text-right p-1.5">Tip</th>
                              <th className="text-center p-1.5">Comp</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.setup_sheet.tools.map((tool, i) => (
                              <tr key={i} className="border-b border-industrial-border/50 text-industrial-text">
                                <td className="p-1.5 font-mono text-industrial-readout">{tool.station}</td>
                                <td className="p-1.5">{tool.description}</td>
                                <td className="p-1.5 text-industrial-text-secondary">{tool.insert || '-'}</td>
                                <td className="p-1.5 text-right font-mono">{tool.nose_radius || '-'}</td>
                                <td className="p-1.5 text-right font-mono">{tool.tip_orient || '-'}</td>
                                <td className="p-1.5 text-center font-mono">{tool.comp_dir || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Work Holding */}
                  {(result.setup_sheet.workholding_op1 || result.setup_sheet.workholding_op2) && (
                    <div>
                      <h5 className="text-xs font-semibold text-industrial-text-secondary uppercase mb-2">Work Holding</h5>
                      <div className="space-y-1 text-xs">
                        {result.setup_sheet.workholding_op1 && (
                          <div className="p-2 bg-industrial-bg rounded border border-industrial-border">
                            <span className="text-industrial-text-secondary">OP1: </span>
                            <span className="text-industrial-text">{result.setup_sheet.workholding_op1}</span>
                          </div>
                        )}
                        {result.setup_sheet.workholding_op2 && (
                          <div className="p-2 bg-industrial-bg rounded border border-industrial-border">
                            <span className="text-industrial-text-secondary">OP2 (Flip): </span>
                            <span className="text-industrial-text">{result.setup_sheet.workholding_op2}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Quality Checks */}
                  {result.setup_sheet.quality_checks && result.setup_sheet.quality_checks.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-industrial-text-secondary uppercase mb-2">Quality Checks</h5>
                      <div className="space-y-1">
                        {result.setup_sheet.quality_checks.map((check, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs p-1.5 bg-industrial-bg rounded border border-industrial-border">
                            <Target className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                            <span className="text-industrial-text">{check.feature}: </span>
                            <span className="font-mono text-industrial-readout">{check.dimension}"</span>
                            {check.tolerance && <span className="text-industrial-text-secondary">+/-{check.tolerance}"</span>}
                            <span className="text-industrial-text-secondary ml-auto">{check.method}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Safety Notes */}
                  {result.setup_sheet.safety_notes && result.setup_sheet.safety_notes.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-industrial-text-secondary uppercase mb-2">Safety Notes</h5>
                      <div className="space-y-1">
                        {result.setup_sheet.safety_notes.map((note, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs p-1.5 bg-amber-500/5 border border-amber-500/20 rounded">
                            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                            <span className="text-amber-300">{note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Collision Detection & Validation Results */}
            {result.validation_issues?.length > 0 && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4" data-testid="validation-results">
                <h4 className="font-heading font-semibold text-industrial-text mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-industrial-accent" />
                  Program Validation & Collision Check
                </h4>
                <div className="space-y-2">
                  {result.validation_issues.map((issue, i) => (
                    <div 
                      key={i}
                      className={`flex items-start gap-2 p-2 rounded text-xs ${
                        issue.severity === 'error' 
                          ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                          : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400'
                      }`}
                    >
                      {issue.severity === 'error' ? (
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <span className="font-semibold uppercase">{issue.severity}: </span>
                        {issue.message}
                        {issue.line > 0 && (
                          <span className="ml-1 opacity-70">(Line {issue.line})</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {result.validation_issues.filter(i => i.severity === 'error').length === 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-green-400">
                    <CheckCircle className="w-3.5 h-3.5" />
                    No critical errors detected - program passed collision check
                  </div>
                )}
              </div>
            )}
            
            {/* No validation issues - show green badge */}
            {result.gcode && (!result.validation_issues || result.validation_issues.length === 0) && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-xs text-green-400">
                <CheckCircle className="w-4 h-4" />
                Program passed collision detection and validation checks
              </div>
            )}
            
            {/* Generate Another Button */}
            <button
              onClick={() => { setFile(null); setFilePreview(null); setResult(null); }}
              className="w-full py-3 border border-industrial-border text-industrial-text rounded-lg hover:bg-industrial-surface-highlight transition-colors"
            >
              Generate Another Program
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CADImportPage;
